import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaClock, FaCalendarAlt, FaArrowRight, FaPlay } from 'react-icons/fa';

const BlogCard = ({ blog, index = 0, className = '', enableHoverEffects = false }) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.6, -0.05, 0.01, 0.99],
        delay: index * 0.15
      }
    },
    hover: {
      y: enableHoverEffects ? -15 : -5,
      scale: enableHoverEffects ? 1.03 : 1.01,
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <motion.div 
      className={`relative group ${className}`}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      whileHover="hover"
      viewport={{ once: true, margin: "-50px" }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <Link to={`/blog/${blog.id}`} className="block">
        <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group-hover:border-transparent h-full">
          {/* Gradient Overlay - Only show when enableHoverEffects is true and only on content area */}
          {enableHoverEffects && (
            <div className="absolute inset-0 top-48 bg-gradient-to-br from-purple-500 via-blue-600 to-cyan-500 opacity-0 group-hover:opacity-95 transition-all duration-500 z-10"></div>
          )}
          
          {/* Glow Effect - Only when hover effects enabled */}
          {enableHoverEffects && (
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-600 blur-xl opacity-30 -m-2"></div>
            </div>
          )}

          {/* Blog Image - Always visible */}
          <div className="relative h-48 overflow-hidden">
            <motion.div
              className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center"
              animate={{
                scale: isHovered ? 1.1 : 1,
              }}
              transition={{ duration: 0.5 }}
            >
              {blog.image ? (
                <img 
                  src={blog.image} 
                  alt={blog.title} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center text-blue-300">
                  <FaPlay className="text-3xl mb-2" />
                  <span className="text-sm font-medium">Blog Article</span>
                </div>
              )}
            </motion.div>

            {/* Floating Date Chip */}
            <motion.div
              className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-gray-700 flex items-center shadow-lg"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 + 0.5, duration: 0.4 }}
            >
              <FaCalendarAlt className="mr-1 text-blue-500" />
              {formatDate(blog.date)}
            </motion.div>

            {/* Read Time Chip */}
            <motion.div
              className="absolute top-4 left-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full px-3 py-1 text-xs font-medium flex items-center shadow-lg"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 + 0.7, duration: 0.4 }}
            >
              <FaClock className="mr-1" />
              {blog.readTime}
            </motion.div>
          </div>

          {/* Content */}
          <div className="relative p-6 z-20">
            {/* Title */}
            <h3 className={`text-xl font-bold mb-3 leading-tight transition-colors duration-300 line-clamp-2 ${
              enableHoverEffects 
                ? 'text-gray-800 group-hover:text-white' 
                : 'text-gray-800 group-hover:text-blue-700'
            }`}>
              {blog.title}
            </h3>

            {/* Summary */}
            <p className={`text-sm leading-relaxed line-clamp-3 mb-4 transition-colors duration-300 ${
              enableHoverEffects 
                ? 'text-gray-600 group-hover:text-white/90' 
                : 'text-gray-600 group-hover:text-gray-800'
            }`}>
              {blog.summary}
            </p>

            {/* Read More Button */}
            <motion.div
              className="flex items-center justify-between"
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: isHovered ? 1 : 0.7, 
                y: isHovered ? 0 : 10 
              }}
              transition={{ duration: 0.3 }}
            >
              <div className={`flex items-center font-medium text-sm transition-colors duration-300 ${
                enableHoverEffects 
                  ? 'text-blue-600 group-hover:text-white' 
                  : 'text-blue-600 group-hover:text-blue-700'
              }`}>
                <span>Read More</span>
                <motion.div
                  animate={{ x: isHovered ? 5 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaArrowRight className="ml-2 text-xs" />
                </motion.div>
              </div>

              {/* Engagement indicator - Only show with hover effects */}
              {enableHoverEffects && (
                <motion.div
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  animate={{
                    scale: isHovered ? [1, 1.2, 1] : 1,
                  }}
                  transition={{
                    duration: 2,
                    repeat: isHovered ? Infinity : 0,
                  }}
                >
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                </motion.div>
              )}
            </motion.div>
          </div>

          {/* Decorative Elements - Only when hover effects enabled */}
          {enableHoverEffects && (
            <>
              <div className="absolute -top-2 -right-2 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-full group-hover:scale-150 transition-transform duration-500"></div>
              <div className="absolute -bottom-2 -left-2 w-16 h-16 bg-gradient-to-tr from-cyan-500/10 to-purple-500/10 rounded-full group-hover:scale-125 transition-transform duration-500"></div>

              {/* Sparkle Effects */}
              <motion.div
                className="absolute top-6 right-6 w-1 h-1 bg-yellow-400 rounded-full"
                animate={{
                  opacity: isHovered ? [0, 1, 0] : 0,
                  scale: isHovered ? [0, 1.5, 0] : 0,
                }}
                transition={{
                  duration: 1.5,
                  repeat: isHovered ? Infinity : 0,
                  delay: 0.3
                }}
              />
              <motion.div
                className="absolute bottom-8 right-8 w-1 h-1 bg-pink-400 rounded-full"
                animate={{
                  opacity: isHovered ? [0, 1, 0] : 0,
                  scale: isHovered ? [0, 1.2, 0] : 0,
                }}
                transition={{
                  duration: 1.5,
                  repeat: isHovered ? Infinity : 0,
                  delay: 0.8
                }}
              />

              {/* Glow lines */}
              <motion.div
                className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                animate={{
                  scaleX: isHovered ? [0, 1] : 0,
                }}
                transition={{ duration: 0.8 }}
              />
              <motion.div
                className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-purple-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                animate={{
                  scaleX: isHovered ? [0, 1] : 0,
                }}
                transition={{ duration: 0.8, delay: 0.2 }}
              />
            </>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default BlogCard; 